import classnames from 'classnames';
import React from 'react';
import { useId } from "react-id-generator";

interface ToggleSwitchProps {
    /** Flag that will add a comestic CSS class that can be used to make the appearance of the checkbox appear "always on" (not greyed out). */
    alwaysOn?: boolean,
    /** CSS class name that will apply on the top level element of the component. */
    className?: string,
    /** Whether or not the switch is active or not. */
    checked?: boolean,
    /** Whether or not the switch is disabled. */
    disabled?: boolean,
    /** The ID prefix of the component, also used for the testId. If this is null, an ID prefix will be generated. */
    idPrefix?: string,
    /** The main label of the switch, displayed to the right when the switch appears "ON". Can be empty. */
    label?: string,
    /** The name property of the underlying checkbox for usage in forms. */
    name?: string,
    /** The optional off label is a supplementary label that appears to the left of the switch when provided and indicates a value for when the switch is "OFF". */
    offLabel?: string,
    /** onChange handler of the switch. Fired when the switch checked state changes. */
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void,
    /** onClick handler of the switch. Fired when the switch is clicked. */
    onClick?: (e: React.MouseEvent<HTMLLabelElement>) => void,
    /** Whether or not the switch can be interacted with. */
    readOnly?: boolean,
    /** Title of the root element, which can be used to set mouseover tooltip. */
    title?: string,
    /** The value of the underlying checkbox of the switch. */
    value?: string
}

/**
 * Attractive looking "iPhone-like" toggle switch that can be used as an alternative look for a checkbox-like control.
 * 
 * @param {object} props 
 */
function ToggleSwitch(props: ToggleSwitchProps) {
    
    const className = classnames('toggle-switch', props.className, { 'always-on': props.alwaysOn }, { 'disabled': props.disabled });
    const idPrefix = props.idPrefix || useId(1, 'toggleSwitch-')[0];

    return (
        <label data-testid={idPrefix + 'Container'} className={className} htmlFor={idPrefix + "Checkbox"} onClick={props.onClick}>
            <input
                checked={props.checked}
                data-testid={idPrefix + "Checkbox"}
                disabled={props.disabled}
                id={idPrefix + "Checkbox"}
                name={props.name}
                onChange={props.onChange}
                readOnly={props.readOnly}
                type="checkbox"
                value={props.value ? props.value : 'true'}
            />
            <i data-testid={idPrefix + 'Title'} title={props.title}></i>
            <span>{props.offLabel}</span>
            <span>{props.label}</span>
        </label>
    );

}

ToggleSwitch.defaultProps = {
    alwaysOn: false,
    checked: false
};

export default ToggleSwitch;